import classNames from 'classnames';
import { Data, UI, Utils } from 'common';

// cherry-pick lodash methods (the right way). DO NOT use import from 'lodash'.
import filter from 'lodash/filter';
import find from 'lodash/find';
import isObject from 'lodash/isObject';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { RoutePath } from '../utils/RoutePath';
import { CancelAccountContext } from './CancelAccount.context';
import {
	CancelQuestionsDropdown2,
	CancelQuestionsDropdown3,
	CancelQuestionsDropdown4,
	CancelReasonForRetention,
} from './CancelAccount.enum';
import DowngradePlanContent from './DowngradePlanContent.component';

const {
	Spinner,
	WrappedButton,
	WrappedButtonType,
	Typography,
	Fineprint,
	Link,
} = UI;
const {
	Logger,
	Session,
	Util,
	withLayoutAndModalContexts,
	Analytics,
	BillingPlans,
} = Utils;
const { ResubmitStatus, ABTestingKeys, PlanIds } = Data;
const { prettyPrintPlan } = BillingPlans;

const RETENTION_PLANS = {
	402: {
		Name: 'Basic Plan',
		MonthlyFee: 14.99,
		Disposition: 'Retained – Changed to Basic Plan',
	},
	382: {
		Name: '0 Shipper Plan',
		MonthlyFee: 0,
		Disposition: 'Retained – Changed to Pay as you Go Plan',
	},
	397: {
		Name: 'Pay as you Go Plan 2',
		MonthlyFee: 0,
		Disposition: 'Retained – Changed to Pay as you Go Plan 2',
	},
	407: {
		Name: 'Basic Plan',
		MonthlyFee: 9.99,
		Disposition: 'Retained – Changed to Basic Retention Plan',
	},
	408: {
		Name: 'Lite Plan',
		MonthlyFee: 4.99,
		Disposition: 'Retained – Changed to Lite Plan',
	},
};

class Downgrade extends Component {
	modalContext = null;

	cancelAccountContext = null;

	constructor(props) {
		super(props);

		this.state = {
			showDowngradeConfirmation: false,
			showSpinner: false,
			plans: [],
			acceptedPlan: null,
			planId: '',
			currentPlanPrice: null,
			currentPlanId: null,
			isGbbUser: false,
			retentionPlanId: null,
		};
	}

	async componentDidMount() {
		const accountInfo = Session.getAccountInfo();
		const { CurrentServiceFees } = Session.getInternalAccountInfo();
		const { internalAccountDetails = {} } = accountInfo;
		const currentPlanId = accountInfo.planDetails.planId;
		let downgradePlanId = null;

		if (localStorage.getItem('hasAcceptedBasicOffer') === 'true') {
			downgradePlanId = 402;
		} else if (localStorage.getItem('hasAcceptedPaygoOffer') === 'true') {
			downgradePlanId = 382;
		} else if (localStorage.getItem('hasAcceptedPaygoOffer2') === 'true') {
			downgradePlanId = 397;
		} else if (
			localStorage.getItem('hasAcceptedRetentionBasicOffer') === 'true'
		) {
			downgradePlanId = 407;
		} else if (
			localStorage.getItem('hasAcceptedRetentionLiteOffer') === 'true'
		) {
			downgradePlanId = 408;
		}

		// Retention flags only pre-select a plan. The billing change happens
		// exclusively from the explicit Confirm click (onConfirmRetentionDowngrade).
		if (downgradePlanId) {
			this.setState({ retentionPlanId: downgradePlanId });
		} else if (
			this.cancelAccountContext.HasViewedDowngrade === false &&
			internalAccountDetails.resubmit?.status ===
				ResubmitStatus.NotInResubmission &&
			(CurrentServiceFees >= 10 ||
				[249, 250, 251, 260].indexOf(currentPlanId) !== -1)
		) {
			// Non-Paygo downgrade
			this.cancelAccountContext.saveCancelAccountInfo({
				HasViewedDowngrade: true,
			});
			this.setState(
				{
					currentPlanPrice: CurrentServiceFees,
					currentPlanId,
					isGbbUser:
						[
							PlanIds.Core,
							PlanIds.Office,
							PlanIds.Essential,
							PlanIds.Ecommerce,
							PlanIds.CoreV380,
							PlanIds.OfficeV381,
							PlanIds.EcommerceV383,
						].indexOf(currentPlanId) !== -1,
				},
				this.processDowngradeOffers
			);
		} else {
			this.goToNextStep();
		}
	}

	clearRetentionFlags = () => {
		localStorage.removeItem('hasAcceptedBasicOffer');
		localStorage.removeItem('hasAcceptedPaygoOffer');
		localStorage.removeItem('hasAcceptedPaygoOffer2');
		localStorage.removeItem('hasAcceptedRetentionBasicOffer');
		localStorage.removeItem('hasAcceptedRetentionLiteOffer');
		localStorage.removeItem('retentionData');
	};

	onCancelRetentionDowngrade = () => {
		const { history } = this.props;

		this.clearRetentionFlags();
		history.push(RoutePath.changePlan);
	};

	onConfirmRetentionDowngrade = async () => {
		const { retentionPlanId: downgradePlanId } = this.state;
		const { modalContext } = this.props;
		const accountInfo = Session.getAccountInfo();
		const plan = RETENTION_PLANS[downgradePlanId];

		this.setState({ showSpinner: true });

		const changePlanResponse = await AccountCommunication.changePlan({
			PlanId: downgradePlanId,
			PromoCode: null,
			Disposition: {
				Id: 1213,
				Description: plan.Disposition,
			},
			CancelReason: {
				Id: null,
				Reason: null,
			},
		});

		this.clearRetentionFlags();

		if (AccountCommunication.isSuccessful(changePlanResponse)) {
			this.setState({ showSpinner: false }, () => {
				this.cancelAccountContext.saveCancelAccountInfo(
					{
						HasViewedDowngrade: true,
						AcceptedDowngrade: true,
					},
					() => {
						this.setState(
							{
								retentionPlanId: null,
								acceptedPlan: {
									PlanId: downgradePlanId,
									Name: plan.Name,
									MonthlyFee: plan.MonthlyFee,
								},
								showDowngradeConfirmation: true,
								showSpinner: false,
							},
							() => {
								// manually set pendingPlanId, as there is a delay when calling loadAccountInfo
								accountInfo.planDetails.pendingPlanId =
									1020000 + downgradePlanId;
							}
						);
					}
				);
			});
		} else {
			this.setState({ showSpinner: false }, () => {
				modalContext.showError({ response: changePlanResponse });
			});
		}
	};

	goToNextStep = () => {
		this.cancelAccountContext.setStep('AddressVerification');
	};

	processDowngradeOffers = () => {
		const context = this.cancelAccountContext;
		const eligiblePlans = [];
		let filteredEligiblePlans = [];
		let planId = '';
		const {
			optimizely,
			Dropdown1Answer,
			Dropdown2Answer,
			Dropdown2Other,
			Dropdown3Answer,
		} = context;
		let decision;
		let variationKey;
		let variationName;
		const { currentPlanId, currentPlanPrice, isGbbUser } = this.state;

		if (isGbbUser) {
			planId = 378;
			eligiblePlans.push(378);
		} else {
			switch (CancelQuestionsDropdown2[context.Dropdown2Answer]) {
				case CancelQuestionsDropdown2.marketplace:
					switch (CancelQuestionsDropdown4[context.Dropdown3Answer]) {
						case CancelQuestionsDropdown4.marketplaces:
							planId = 278;
							eligiblePlans.push(278);
							break;
						default:
							planId = 285278;
							eligiblePlans.push(285, 278);
							break;
					}
					break;
				case CancelQuestionsDropdown2.usps:
					planId = 248;
					eligiblePlans.push(248);
					break;
				case CancelQuestionsDropdown2.otherCarrier:
					planId = 278;
					eligiblePlans.push(278);
					break;
				case CancelQuestionsDropdown2.multiCarrier:
					switch (CancelQuestionsDropdown3[context.Dropdown3Answer]) {
						case CancelQuestionsDropdown3.other:
							planId = 278;
							eligiblePlans.push(278);
							break;
						default:
							this.goToNextStep();
							break;
					}
					break;
				case CancelQuestionsDropdown2.closed:
					this.goToNextStep();
					break;
				default:
					if (context.cancelAccountInfo.TotalPostagePurchased < 50) {
						decision = optimizely.decide(
							ABTestingKeys.cancelaccountdowngrade.flagName
						);
						if (decision && decision.enabled) {
							variationKey = isObject(decision) ? decision.variationKey : '';
							variationName =
								variationKey ===
								ABTestingKeys.cancelaccountdowngrade.experimentKey
									? 'Experiment Group'
									: 'Control Group';

							Analytics.trackEvent(
								'Experiment Viewed',
								{
									experiment_id: decision.flagKey,
									experiment_name: decision.flagKey.replaceAll('_', ' '),
									variation_id: variationKey.replaceAll('_', ' '),
									variation_name: variationName,
								},
								Analytics.agent.segment
							);

							if (
								variationKey ===
								ABTestingKeys.cancelaccountdowngrade.experimentKey
							) {
								planId = 100;
								eligiblePlans.push(100);
							} else {
								planId = 182;
								eligiblePlans.push(182);
							}
							this.cancelAccountContext.saveCancelAccountInfo({
								offeredPlanId: planId,
							});
						} else {
							planId = 182;
							eligiblePlans.push(182);
						}
					} else {
						planId = 100;
						eligiblePlans.push(100);
					}
					break;
			}
		}

		Analytics.trackEvent(
			'cancel_account_viewed_downgrade',
			{
				planId,
				currentPlanId,
				currentPlanPrice,
				dropdown1Answer: Dropdown1Answer,
				dropdown2Answer: Dropdown2Answer,
				dropdown2Other: Dropdown2Other,
				dropdown3Answer: Dropdown3Answer,
			},
			Analytics.agent.segment
		);

		filteredEligiblePlans = filter(
			context.cancelAccountInfo.Plans,
			record => eligiblePlans.indexOf(record.PlanId) !== -1
		);

		this.setState({
			plans: filteredEligiblePlans,
			planId,
		});
	};

	processPromoCode = planId => {
		if (planId === 278 || planId === 285) {
			return 'XMT8-58QN';
		}

		return '';
	};

	onAcceptOffer = async e => {
		const { modalContext } = this.props;
		const { plans, currentPlanId, currentPlanPrice } = this.state;
		const planId = parseInt(e.currentTarget.getAttribute('planid'));
		const acceptedPlan = find(plans, ['PlanId', planId]);
		const context = this.cancelAccountContext;
		const {
			optimizely,
			Dropdown1Answer,
			Dropdown2Answer,
			Dropdown2Other,
			Dropdown3Answer,
		} = context;
		const { closeRequestedDate } = Session.getAccountInfo();

		Logger.saveActionData('Accept Offer Button Clicked', {
			Plan: acceptedPlan,
		});

		// per WEBAPPS-26497, show loading mask
		this.setState({
			showSpinner: true,
		});

		const abTestProperties = {
			revenue: parseInt(acceptedPlan.MonthlyFee * 100),
			offeredPlanId: acceptedPlan.PlanId,
			currentPlanId,
			currentPlanPrice,
			dropdown1Answer: Dropdown1Answer,
			dropdown2Answer: Dropdown2Answer,
			dropdown2Other: Dropdown2Other,
			dropdown3Answer: Dropdown3Answer,
		};

		optimizely.track('cancel_account_downgrade_selected', abTestProperties);
		Analytics.trackEvent(
			'cancel_account_downgrade_selected',
			abTestProperties,
			Analytics.agent.segment
		);

		// If account is pending closure, reactivate plan before changing plan
		if (closeRequestedDate) {
			const reactivatePlanResponse =
				await AccountCommunication.reactivatePlan();

			Analytics.trackEvent(
				'reactivate_plan',
				{
					type: 'downgrade',
					selectedPlan:
						acceptedPlan && acceptedPlan.Name ? acceptedPlan.Name : '',
					selectedPlanId:
						acceptedPlan && acceptedPlan.PlanId ? acceptedPlan.PlanId : '',
				},
				Analytics.agent.segment
			);

			if (!AccountCommunication.isSuccessful(reactivatePlanResponse)) {
				modalContext.showError({ response: reactivatePlanResponse });
				return;
			}
		}

		const changePlanResponse = await AccountCommunication.changePlan({
			PlanId: planId,
			PromoCode: this.processPromoCode(planId),
			Disposition: {
				Id: planId === 278 ? 1214 : 1213,
				Description:
					planId === 278
						? 'Retained – Changed to $15.99 Shipper Plan'
						: 'Retained – Changed to 0 Shipper Plan',
			},
			CancelReason: {
				Id: CancelReasonForRetention[this.cancelAccountContext.Dropdown2Answer],
				Reason:
					CancelQuestionsDropdown2[this.cancelAccountContext.Dropdown2Answer],
			},
			newPlanPrice: acceptedPlan.MonthlyFee,
		});

		if (AccountCommunication.isSuccessful(changePlanResponse)) {
			this.setState(
				{
					acceptedPlan,
					showDowngradeConfirmation: true,
					showSpinner: false,
				},
				() => {
					this.cancelAccountContext.saveCancelAccountInfo({
						HasViewedDowngrade: true,
						AcceptedDowngrade: true,
					});
				}
			);
		} else {
			this.setState(
				{
					showSpinner: false,
				},
				() => {
					modalContext.showError({ response: changePlanResponse });
				}
			);
		}
	};

	getHeader = plan => {
		const { isGbbUser } = this.state;
		if (isGbbUser) {
			return (
				<h2 className="secondary-header">
					{`You qualify for our ${prettyPrintPlan(plan.Name)} - $${
						plan.MonthlyFee
					}/Month`}
				</h2>
			);
		} else {
			return (
				<h2 className="secondary-header">
					{`${prettyPrintPlan(plan.Name)} - $${plan.MonthlyFee}/Month`}{' '}
				</h2>
			);
		}
	};

	renderPlanOffers = () => {
		const { plans, planId, isGbbUser } = this.state;
		const className = classNames('downgrade-plan-option', {
			'p-md-6 p-col': plans.length > 1,
			'p-md-12 p-col': plans.length <= 1,
		});

		return (
			plans.length > 0 && (
				<div>
					{isGbbUser && (
						<div className="primary-header">
							Great news! We have a solution for you.{' '}
						</div>
					)}
					<div className="p-grid">
						<div className="p-col-12">
							<DowngradePlanContent planId={planId} />
						</div>
						<div className="downgrade-offers p-col-12">
							<div className="p-grid">
								{plans.map(plan => (
									<div key={plan.PlanId} className={className}>
										<div className="content-container">
											{this.getHeader(plan)}
											<p
												className="content"
												// eslint-disable-next-line
												dangerouslySetInnerHTML={{ __html: plan.Content }}
											/>
											<div className="button-container">
												<WrappedButton
													buttonType={WrappedButtonType.Primary}
													label="Accept Offer"
													onClick={this.onAcceptOffer}
													dataQA="accept"
													planid={plan.PlanId}
												/>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
						<div className="cancel-button p-col-12">
							<WrappedButton
								buttonType={WrappedButtonType.Secondary}
								className="text-only"
								label="Continue to cancel"
								onClick={this.goToNextStep}
								dataQA="cancel"
							/>
						</div>
					</div>
				</div>
			)
		);
	};

	renderConfirmation = () => {
		const { acceptedPlan } = this.state;
		const planId = acceptedPlan.PlanId;
		const prettyPlanName = prettyPrintPlan(acceptedPlan.Name);
		const fee = `$${acceptedPlan.MonthlyFee}`;
		const planWithMonthlyFee =
			planId !== 100 && planId !== 182 && planId !== 382 && planId !== 397;
		const planWithCostText1 = planWithMonthlyFee
			? ` which includes a ${fee} monthly service fee`
			: '';
		const planWithCostText2 = planWithMonthlyFee
			? ' Please note that it may take up to 48 business hours for the new rates to be reflected in your account.'
			: '';
		const todaysDate = new Date().getDate();
		const billingDay = Session.getInternalAccountInfo().BillingDayOfMonth;
		const sameDayBilling = todaysDate === billingDay;
		const billingDate = `${new Date().getMonth() + 1}/${billingDay}/${new Date()
			.getFullYear()
			.toString()
			.substr(2, 2)}`;
		const nextBillingDate = Util.getBillingDate(billingDay, 'numeric');
		const {
			planDetails: { currentPlanServiceFee },
		} = Session.getAccountInfo();
		const contactNumber = Util.getFraudNumber();

		return planId === 382 || planId === 397 ? (
			<div className="confirmation-container">
				<div className="header">
					<div className="success-icon" />
					<Typography.H3>Plan Change Successful!</Typography.H3>
				</div>
				{planId === 382 ? (
					// Paygo downgrade
					<Typography.P>
						Access your current plan until {`${nextBillingDate}`}. On this date,
						you will receive a final bill of ${currentPlanServiceFee} + any
						taxes and your plan change will be effective. Any funds in your
						balance will be used for postage purchases. After that, your credit
						card will be charged for each print.
					</Typography.P>
				) : (
					// Paygo2 downgrade
					<Typography.P>
						Access your current plan until {`${nextBillingDate}`}. On this date,
						you will receive a final bill of ${currentPlanServiceFee} + taxes
						and your plan change will be effective. Your new subscription fee
						will be $10.99/month once the plan change is in effect.
					</Typography.P>
				)}
				<Fineprint type="information">
					Questions?{' '}
					<Link href="https://help.stamps.com/hc/en-us/articles/20821937716507-Close-Your-Account#UUID-a9c3392c-51d5-978d-e25a-9907d4151e91_section-idm234725993034538">
						Learn more
					</Link>{' '}
					or contact support at 888-434-0055 M-F, 8am to 8pm CT.
				</Fineprint>
			</div>
		) : (
			<div data-qa="downgrade-confirmed" {...{ dataqa: 'downgrade-confirmed' }}>
				<h1 className="primary-header">Request Processed</h1>
				{sameDayBilling ? (
					<div>
						<p className="confirmation-content">
							<b>
								{`Your subscription plan has been changed to the Stamps.com ${prettyPlanName} ${planWithCostText1}.`}
							</b>
						</p>
						<p>
							{`Your bill date for your current plan is today, ${billingDate} and the billing amount will not change.
									Your next bill date will be ${nextBillingDate} and will be ${fee} each month on your billing anniversary.`}
						</p>
					</div>
				) : (
					<div>
						<p>
							<b>
								{`Your subscription plan has been changed to the Stamps.com ${prettyPlanName} ${planWithCostText1}.
									Since you are changing your plan, you will have access to your prior account features through the end of
									your billing cycle: ${nextBillingDate}. ${planWithCostText2}`}
							</b>
						</p>
						<p>
							{`Your next bill date is ${nextBillingDate} and will be for ${fee}.
									You will be billed this amount each month on your billing anniversary.`}
						</p>
					</div>
				)}
				<p>{`If you have any questions or need further assistance, please contact customer support toll-free at ${contactNumber}, Monday – Friday, 6 a.m – 6 p.m Pacific Time.`}</p>
			</div>
		);
	};

	renderRetentionConfirm = () => {
		const { retentionPlanId } = this.state;
		const plan = RETENTION_PLANS[retentionPlanId];
		const billingDay = Session.getInternalAccountInfo().BillingDayOfMonth;
		const nextBillingDate = Util.getBillingDate(billingDay, 'numeric');

		return (
			<div className="confirmation-container" data-qa="retention-confirm">
				<Typography.H3>Confirm your plan change</Typography.H3>
				<Typography.P>
					You are about to switch to the {plan.Name}. You will have access to
					your current plan until {nextBillingDate}, when your plan change
					becomes effective.
				</Typography.P>
				<div className="button-container">
					<WrappedButton
						buttonType={WrappedButtonType.Primary}
						label="Confirm"
						onClick={this.onConfirmRetentionDowngrade}
						dataQA="confirm-retention-downgrade"
					/>
					<WrappedButton
						buttonType={WrappedButtonType.Secondary}
						className="text-only"
						label="Cancel"
						onClick={this.onCancelRetentionDowngrade}
						dataQA="cancel-retention-downgrade"
					/>
				</div>
			</div>
		);
	};

	render() {
		const { showSpinner, showDowngradeConfirmation, retentionPlanId } =
			this.state;

		let content = this.renderPlanOffers();
		if (showDowngradeConfirmation) {
			content = this.renderConfirmation();
		} else if (retentionPlanId) {
			content = this.renderRetentionConfirm();
		}

		return (
			<CancelAccountContext.Consumer>
				{context2 => {
					this.cancelAccountContext = context2;
					return (
						<div className="cancel-account-downgrade">
							{showSpinner && <Spinner msg="Changing plan..." />}
							{content}
						</div>
					);
				}}
			</CancelAccountContext.Consumer>
		);
	}
}

export default withLayoutAndModalContexts(Downgrade);
